import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
// import { useIntl, getLocale } from 'umi';
import moment from 'moment';
import Filter from '@/pages/intelligence/components/Filter';
import useFilterExtend from '@/pages/intelligence/hooks/useStoreRankFilterExtend';
import { feachTimeTypeToFilterTimeType } from '@/pages/intelligence/utils/common';
import { getInitialFormValue } from './utils';
import {
  // 日期筛选器
  RANGE_TIME as RANGE_TIME_SELECT_FILTER,
  // 发布时间筛选器
  RELEASE_TIME as RELEASE_TIME_SELECT_FILTER,
  // 级联国家筛选器
  REGION as REGION_SELECT_FILTER,
} from '@/pages/intelligence/components/Filter/FilterBase/_config';
import { DEFAULT_FORM_VALUE, STATIC_FILTER_CONFIGS } from './config';

import type { IBaseProps, IRegion, IDateTypely } from '@/pages/intelligence/types/common';

interface IPCStoreRankMoreFilterProps extends IBaseProps {
  // 筛选器右侧的info提示
  iconText?: React.ReactNode;
  isOperate: boolean;
  granularity?: IDateTypely;
  startDate?: string;
  endDate?: string;
  showRegionSelect?: boolean;
  regionOptions?: IRegion[] | null;
  onConfirm?: (payload: any) => void;
  onReset?: () => void;
}

const PCStoreRankMoreFilter: React.FC<IPCStoreRankMoreFilterProps> = (props) => {
  const {
    style,
    className,
    isOperate,
    iconText,
    granularity,
    startDate,
    endDate,
    showRegionSelect = false,
    regionOptions,
    onConfirm,
    onReset,
  } = props;

  // const { formatMessage } = useIntl();

  /**
   * 继承逻辑在这写
   * formValue改成用ref，可提供给父组件回填时间， 或添加继承逻辑提供formValue给父组件
   */
  const { filterExtend, setFilterExtend, resetFilterExtend, setDetailFilterExtend, resetDetailFilterExtend } = useFilterExtend({});

  // 筛选器的初始值
  const initialFormValue = getInitialFormValue(isOperate, filterExtend, DEFAULT_FORM_VALUE);

  // 筛选器UI渲染需要的value
  const formValue = useRef<any>(initialFormValue);

  const [inputList, setInputList] = useState([
    STATIC_FILTER_CONFIGS.RANGE_TIME,
    STATIC_FILTER_CONFIGS.REGION,
    STATIC_FILTER_CONFIGS.RELEASE_TIME,
  ]);

  const updateRegionFilterConfig = (
    inputList: any[],
    cfg: { showRegionSelect: boolean; regionOptions?: IRegion[] | null },
  ) => {
    const { showRegionSelect, regionOptions } = cfg || {};
    const regionFilterInput = inputList?.find((item) => item?.key === REGION_SELECT_FILTER);
    if (regionFilterInput) {
      regionFilterInput.visible = showRegionSelect ? 1 : 2;
    }
    if (regionFilterInput && Reflect.has(regionFilterInput, 'options')) {
      regionFilterInput.options = regionOptions ?? [];
    }
  };

  /**
   * 更新各个筛选器的options数据
   */
  useEffect(() => {
    const inputListTmp = [...inputList];
    // 更新国家筛选器配置
    updateRegionFilterConfig(inputListTmp, { showRegionSelect, regionOptions });
    setInputList(inputListTmp);
  }, [showRegionSelect, regionOptions]);

  /**
   * 同步父组件传入的时间
   */
  useEffect(() => {
    const rangeTimeValue = formValue.current?.[RANGE_TIME_SELECT_FILTER];
    formValue.current = {
      ...formValue.current,
      [RANGE_TIME_SELECT_FILTER]: {
        type: granularity ? feachTimeTypeToFilterTimeType(granularity) : rangeTimeValue?.type ?? 'date',
        value: startDate && endDate ? [moment(startDate), moment(endDate)] : rangeTimeValue?.value ?? [],
      },
    };
  }, [granularity, startDate, endDate]);

  /**
   * 筛选器confirm的回调
   * @param value 筛选器数据（筛选器中各个组件UI渲染需要的value）
   * @param apiValue 处理后的筛选器数据，为了更方便调用接口，针对高频特殊筛选器的数据做了数据格式转换（将筛选器原始的数据转换为更方便接口入参的数据格式，同时也会保留筛选器最原始的数据，用于筛选器继承）
   */
  const handleConfirm = (value: any, apiValue: any) => {
    formValue.current = value;
    const region = apiValue?.[REGION_SELECT_FILTER];
    const rangeTime = apiValue?.[RANGE_TIME_SELECT_FILTER];
    const releaseTime = apiValue?.[RELEASE_TIME_SELECT_FILTER];

    const filterTmp = {
      source: '',
      region_type: region?.region_type ?? 'market',
      regions: showRegionSelect && region?.markets?.length > 0 ? region?.markets : ['global'],

      genre: [],
      sub_genre: [],
      genre_source: 'iegg',
      iegg_genre_req: [],

      granularity: rangeTime?.granularity ?? 'daily',
      start_date: rangeTime?.start_date ?? '',
      end_date: rangeTime?.end_date ?? '',
      start_release_time: releaseTime?.start_release_time ?? '',
      end_release_time: releaseTime?.end_release_time ?? '',
    };
    onConfirm?.(filterTmp);
    setFilterExtend(apiValue);
    setDetailFilterExtend(apiValue)
  };

  const handleReset = () => {
    formValue.current = {
      ...DEFAULT_FORM_VALUE,
      [RANGE_TIME_SELECT_FILTER]: {
        type: 'date',
        value: [moment(), moment()],
      },
    };
    resetFilterExtend();
    resetDetailFilterExtend()
    onReset?.();
  };

  return (
    <Filter
      style={style}
      className={classnames('dbi-store-rank-more-filter dbi-pc-store-rank-more-filter', className)}
      iconText={iconText}
      showButton={false}
      inputList={inputList}
      defaultValue={DEFAULT_FORM_VALUE}
      value={formValue.current}
      onConfirm={handleConfirm}
      onReset={handleReset}
    />
  );
};

export default PCStoreRankMoreFilter;
